import { Stack, Text, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useTranslation } from '@hotjar/translations';
import unstructuredAddressSvg from '../../../resources/images/unstructured-address-migration.svg';
import unstructuredAddressExtendedSvg from '../../../resources/images/unstructured-address-migration-extended.svg';
import { FlatCard } from '../../flat-card/FlatCard';

interface Props {
  unstructuredAddress?: string;
  companyName?: string;
  taxNumber?: string | null;
  className?: string;
  extendedVersion?: boolean;
}

const S = {
  Card: styled(FlatCard)<{ extended: boolean }>`
    ${({ extended }) =>
      extended
        ? css`
            flex-direction: row;
            border-radius: ${tokens.spacer.x1pt5};
            overflow: hidden;
            padding: 0;
          `
        : css`
            width: 246px;
            gap: ${tokens.spacer.x1};
          `};
  `,
  IllustationContainer: styled.div<{ extended: boolean }>`
    ${({ extended }) =>
      extended
        ? css`
            min-width: 170px;
            background-color: ${tokens.color.blossom_300};
            overflow: hidden;
          `
        : css`
            align-self: flex-start;
            height: 123px;
            margin-bottom: ${tokens.spacer.x2};
          `};
  `,
  Illustration: styled.img`
    height: 100%;
    width: 100%;
    object-fit: cover;
  `,
  TextContainer: styled(Stack)<{ extended: boolean }>`
    ${({ extended }) => extended && `padding: ${tokens.spacer.x2};`}
  `,
  UnstructuredAddress: styled(Text)`
    white-space: pre-line;
  `,
  ExtraDetails: styled.div`
    display: flex;
    flex-direction: column;
  `,
};

export function UnstructuredAddressInfo({
  unstructuredAddress,
  companyName,
  taxNumber,
  className,
  extendedVersion = false,
}: Props): JSX.Element {
  const { t } = useTranslation('checkout', {
    keyPrefix: 'invoices.invoice-details-modal.unstructured-address',
  });
  const textType = extendedVersion ? 'caption' : 'body2';

  return (
    <S.Card className={className} extended={extendedVersion}>
      <S.IllustationContainer extended={extendedVersion}>
        <S.Illustration
          src={extendedVersion ? unstructuredAddressExtendedSvg : unstructuredAddressSvg}
          alt=""
        />
      </S.IllustationContainer>
      <S.TextContainer spacing="x1" direction="column" extended={extendedVersion}>
        <Text textType="subtitle2">
          {t('title', { context: extendedVersion ? 'extended' : undefined })}
        </Text>
        {extendedVersion && <Text textType={textType}>{t('description')}</Text>}
        <S.UnstructuredAddress textType={textType}>{unstructuredAddress}</S.UnstructuredAddress>
        <S.ExtraDetails>
          <Text textType={textType}>
            {t('organization-name')} {companyName}
          </Text>
          {taxNumber !== null && taxNumber !== '' && (
            <Text textType={textType}>
              {t('tax-number')} {taxNumber}
            </Text>
          )}
        </S.ExtraDetails>
      </S.TextContainer>
    </S.Card>
  );
}
